
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Helmet } from "react-helmet";

export default function FAQs() {
  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Frequently Asked Questions - UnitCodex</title>
        <meta name="description" content="Find answers to common questions about historical units, measurement conversions, and the stories behind different measurement systems." />
        <link rel="canonical" href="https://unitcodex.com/faqs" />
      </Helmet>
      
      <h1 className="text-4xl font-cinzel text-center mb-8">Frequently Asked Questions</h1>
      
      <div className="max-w-4xl mx-auto space-y-6">
        <Card className="bg-manuscript-background/90">
          <CardContent className="p-6">
            <h2 className="text-2xl font-cinzel mb-6">General Questions</h2>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="what-is-unitcodex">
                <AccordionTrigger className="text-lg font-semibold">What is UnitCodex and how does it work?</AccordionTrigger>
                <AccordionContent>
                  UnitCodex is a comprehensive unit conversion tool that combines accurate conversions with historical context. Our platform helps you convert between different units of measurement while learning about their fascinating origins and evolution through history.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="accuracy">
                <AccordionTrigger className="text-lg font-semibold">How accurate are the unit conversions?</AccordionTrigger>
                <AccordionContent>
                  Our conversions are based on internationally recognized standards and are regularly verified for accuracy. However, for critical applications requiring precise measurements, we recommend cross-referencing with official sources or professional tools.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="historical-info">
                <AccordionTrigger className="text-lg font-semibold">Where does the historical information come from?</AccordionTrigger>
                <AccordionContent>
                  Our historical information is carefully researched from reputable academic sources, historical documents, and verified publications. We continuously update our database as new historical research becomes available.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <Card className="bg-manuscript-background/90">
          <CardContent className="p-6">
            <h2 className="text-2xl font-cinzel mb-6">Unit Conversion Questions</h2>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="common-conversions">
                <AccordionTrigger className="text-lg font-semibold">What are the most common unit conversions people look for?</AccordionTrigger>
                <AccordionContent>
                  The most frequently requested conversions include:
                  <ul className="list-disc pl-6 mt-2 space-y-1">
                    <li>Metric to Imperial length conversions (meters to feet)</li>
                    <li>Weight conversions (kilograms to pounds)</li>
                    <li>Temperature conversions (Celsius to Fahrenheit)</li>
                    <li>Area conversions (square meters to square feet)</li>
                    <li>Volume conversions (liters to gallons)</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="ancient-units">
                <AccordionTrigger className="text-lg font-semibold">Can I convert ancient or historical units of measurement?</AccordionTrigger>
                <AccordionContent>
                  Yes! We specialize in historical units of measurement. You can convert ancient units like cubits, Roman miles, talents, and more. Each conversion includes historical context about how these units were used in their time period.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="metric-imperial">
                <AccordionTrigger className="text-lg font-semibold">Why do some countries use metric while others use imperial units?</AccordionTrigger>
                <AccordionContent>
                  The use of metric vs. imperial units is largely historical. The metric system was introduced during the French Revolution and gradually adopted worldwide due to its systematic nature. The United States, Myanmar, and Liberia are the only countries that haven't fully adopted the metric system, primarily due to historical and economic factors.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <Card className="bg-manuscript-background/90">
          <CardContent className="p-6">
            <h2 className="text-2xl font-cinzel mb-6">Historical Measurement Questions</h2>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="first-measurements">
                <AccordionTrigger className="text-lg font-semibold">What were the earliest forms of measurement?</AccordionTrigger>
                <AccordionContent>
                  The earliest measurements were based on human body parts and natural objects. The cubit (length from elbow to fingertip), foot, and palm were among the first standardized units. Ancient civilizations in Egypt, Mesopotamia, and the Indus Valley developed these systems as early as 3000 BCE.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="standardization">
                <AccordionTrigger className="text-lg font-semibold">When did measurement units become standardized?</AccordionTrigger>
                <AccordionContent>
                  Global standardization of measurements began in the late 18th century with the metric system. However, different regions had their own standards much earlier. For example, the English yard was standardized in the 12th century by King Henry I. The modern international system of units (SI) was adopted in 1960.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="regional-variations">
                <AccordionTrigger className="text-lg font-semibold">Why did different regions develop different units of measurement?</AccordionTrigger>
                <AccordionContent>
                  Different units developed based on local needs, trade practices, and cultural factors. For example, maritime nations developed nautical units, while agricultural societies created land measurement units based on practical farming needs. Trade between regions often led to complex conversion systems.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        <Card className="bg-manuscript-background/90">
          <CardContent className="p-6">
            <h2 className="text-2xl font-cinzel mb-6">Technical Questions</h2>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="offline-use">
                <AccordionTrigger className="text-lg font-semibold">Does UnitCodex work offline?</AccordionTrigger>
                <AccordionContent>
                  The core conversion functionality works entirely in your browser, but you need an internet connection to access the historical information and latest updates to our conversion database.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="mobile-support">
                <AccordionTrigger className="text-lg font-semibold">Can I use UnitCodex on my mobile device?</AccordionTrigger>
                <AccordionContent>
                  Yes! UnitCodex is fully responsive and works on all modern mobile devices and tablets. The interface automatically adjusts to provide the best experience on your screen size.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="data-privacy">
                <AccordionTrigger className="text-lg font-semibold">How does UnitCodex handle my data?</AccordionTrigger>
                <AccordionContent>
                  We prioritize your privacy. All conversions are performed locally in your browser, and we don't store any conversion data. You can review our complete privacy policy for more details about how we handle data.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
